import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Loader2, RefreshCw, CalendarClock, ListChecks, AlertTriangle, FileWarning } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export const ELIG_STATUS = {
  on_track: { label: "On track", cls: "bg-[#E6F0EB] text-[#34745C]" },
  at_risk: { label: "At risk", cls: "bg-[#FDF3E1] text-[#C48231]" },
  action_required: { label: "Action required", cls: "bg-[#FCEAE8] text-[#B54D40]" },
};

export const EligibilityPanel = () => {
  const [record, setRecord] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => api.get("/eligibility").then((res) => setRecord(res.data)), []);
  useEffect(() => {
    load();
  }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/eligibility/evaluate");
      setRecord(data);
      toast.success("Eligibility check complete");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Eligibility check failed");
    } finally {
      setRunning(false);
    }
  };

  const r = record?.report;
  const status = r ? ELIG_STATUS[r.eligibility_status] || ELIG_STATUS.at_risk : null;

  return (
    <section data-testid="eligibility-panel" className="bg-white border border-[#E8E6E1] rounded-xl p-6 md:p-8 fade-in-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#D6E0D9]/50 rounded-md flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#2E5C50]" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="text-lg font-heading font-semibold">Eligibility & Compliance</h2>
            {record && (
              <p className="text-xs text-[#57605A]">
                Last checked {new Date(record.created_at).toLocaleString()} · {record.document_count} document(s)
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {status && (
            <span data-testid="eligibility-status" className={`${status.cls} px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider`}>
              {status.label}
            </span>
          )}
          <button
            data-testid="run-eligibility-btn"
            onClick={run}
            disabled={running}
            className="flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <RefreshCw className="w-4 h-4" strokeWidth={1.5} />}
            {running ? "Evaluating…" : record ? "Re-run check" : "Run eligibility check"}
          </button>
        </div>
      </div>

      {!r && !running && (
        <p className="mt-4 text-sm text-[#57605A]">
          Run an eligibility check and Nexari will evaluate your documents — approval likelihood, missing requirements, renewal timelines and what to do next.
        </p>
      )}
      {running && !r && (
        <p className="mt-4 text-sm text-[#C48231]">Nexari is reviewing your documents — this takes about 20–30 seconds…</p>
      )}

      {r && (
        <div className="mt-6 space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Approval likelihood:</span>
            <span data-testid="approval-likelihood" className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
              r.approval_likelihood === "high" ? "bg-[#E6F0EB] text-[#34745C]" : r.approval_likelihood === "low" ? "bg-[#FCEAE8] text-[#B54D40]" : "bg-[#FDF3E1] text-[#C48231]"
            }`}>
              {r.approval_likelihood}
            </span>
          </div>
          <p className="leading-relaxed text-[#1C211F]">{r.status_summary}</p>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">
                <FileWarning className="w-4 h-4" strokeWidth={1.5} /> Missing requirements
              </h3>
              {r.missing_requirements?.length > 0 ? (
                <ul data-testid="missing-requirements" className="mt-3 space-y-2">
                  {r.missing_requirements.map((m, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm leading-relaxed">
                      <AlertTriangle className="w-4 h-4 text-[#B54D40] shrink-0 mt-0.5" strokeWidth={1.5} />
                      {m}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-[#34745C]">Nothing missing — documentation is complete.</p>
              )}
            </div>
            <div>
              <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">
                <CalendarClock className="w-4 h-4" strokeWidth={1.5} /> Renewal timeline
              </h3>
              {r.renewal_timeline?.length > 0 ? (
                <ul data-testid="renewal-timeline" className="mt-3 space-y-2">
                  {r.renewal_timeline.map((t, i) => (
                    <li key={i} className="flex items-start justify-between gap-3 text-sm bg-[#F9F7F4] border border-[#E8E6E1] rounded-md px-3 py-2">
                      <span>{t.item}</span>
                      <span className="font-semibold text-[#C48231] whitespace-nowrap">{t.due}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-[#57605A]">No upcoming renewals identified.</p>
              )}
            </div>
          </div>

          <div className="bg-[#F9F7F4] border border-[#E8E6E1] rounded-lg p-4">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">Risk assessment</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#1C211F]">{r.risk_assessment}</p>
          </div>

          <div>
            <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">
              <ListChecks className="w-4 h-4" strokeWidth={1.5} /> Recommended actions
            </h3>
            <ol data-testid="recommended-actions" className="mt-3 space-y-2">
              {(r.recommended_actions || []).map((a, i) => (
                <li key={i} className="flex items-start gap-3 text-sm leading-relaxed">
                  <span className="w-5 h-5 bg-[#2E5C50] text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                  {a}
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </section>
  );
};
